import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import styles from './StreamingText.module.css';

/**
 * Ported from spectrum-ui's `StreamingText` block — the unmodified source sits
 * in `reference/spectrum-ui/ai-assistants/` for diffing. Upstream is Tailwind +
 * lucide-react; nothing of either survives here. Every value resolves to a
 * token, the one icon comes from the Figma icon set, and the type goes through
 * `Text` wherever the colour is not a state.
 *
 * Upstream ships two variants, `Answer` and `Sources`. Only **Sources** is
 * ported, so there is no `variant` prop. `Answer` collapses the citation list
 * into three overlapping index bubbles and an "N sources" count; `Sources`
 * lists them, which is the variant that actually says where the answer came
 * from. Carrying both would mean drawing eight Figma variants to document a
 * summary nobody asked for.
 *
 * NO FIGMA NODE AT THE TIME OF WRITING — this ran code-first, like the other two
 * blocks on the AI Blocks page. See the stylesheet for the numbers that carry
 * the layout, and `parity.expected.json` once the node exists.
 */

/** One cited source. Upstream's `Citation`, minus `snippet` and `favicon`, which the Sources variant never reads. */
export type Citation = {
  id: string;
  /** The number printed in the marker, and in `[n]` inside `text`. 1-based upstream. */
  index: number;
  url: string;
  title: string;
  /**
   * What to print beside the title instead of the URL's hostname. Not in
   * upstream: DeepDive AI cites the app's own modules, whose "URLs" are hash
   * routes with no host to read — `#brand-health` would print as itself.
   */
  host?: string;
};

/** Upstream's `SuggestedPrompt`. */
export type SuggestedPrompt = {
  id: string;
  label: string;
  /** What to send if it differs from the label shown. */
  prompt?: string;
};

export type StreamingTextProps = {
  /**
   * The answer. Two inline forms are read out of it: `**bold**` becomes a
   * `<strong>`, and `[1]` becomes a citation marker. Everything else is
   * literal — this is deliberately not a markdown renderer.
   */
  text: string;
  /** Shows the caret and withholds the sources and follow-ups until the answer has landed. */
  streaming?: boolean;
  citations?: Citation[];
  followUps?: SuggestedPrompt[];
  onFollowUp?: (prompt: SuggestedPrompt) => void;
  /**
   * Figma `Chart` — the 224-tall slot between the sources and the follow-ups
   * (777:2009, `Chart=True`). Withheld while streaming, as the sources are: the
   * slot is hidden in both `Streaming=True` variants.
   */
  chart?: ReactNode;
  /**
   * Figma `State=Hover` — the `Message Actions` instance at the foot of the
   * block, drawn while streaming and once settled alike. A slot rather than the
   * molecule itself so the host owns copy, feedback and regenerate.
   */
  actions?: ReactNode;
  /**
   * Drop the 520 ceiling and fill the column. The DeepDive AI screens draw the
   * instance at 687 (the chat column's 7 tracks), which is wider than the set.
   */
  fill?: boolean;
  className?: string;
};

/** Splits on the two inline forms, keeping the delimiters as their own segments. */
const SEGMENTS = /(\*\*[^*]+\*\*|\[\d+\])/g;

/**
 * Upstream calls `new URL(url).hostname` bare, which throws and takes the whole
 * list down on one malformed href. A source with an unparseable URL is worth
 * showing with its raw string rather than not showing the answer.
 */
function hostOf(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** How many rows still get a longer entry delay before they all arrive together. */
const MAX_STAGGER_STEPS = 4;

export function StreamingText({
  text,
  streaming = false,
  citations = [],
  followUps = [],
  onFollowUp,
  chart,
  actions,
  fill = false,
  className,
}: StreamingTextProps) {
  const segments = text.split(SEGMENTS).filter(Boolean);
  const settled = !streaming;
  const hasChart = chart !== undefined && chart !== null && chart !== false;
  const hasActions = actions !== undefined && actions !== null && actions !== false;

  return (
    /*
     * `aria-busy` replaces upstream's `aria-hidden` on the paragraph. Upstream
     * hides the whole answer from assistive tech while it streams and never
     * announces it afterwards, so a screen reader user gets the answer only if
     * they happen to re-read the region. Marking the region busy says the same
     * thing without withholding the text. Not in Figma.
     */
    <div
      className={[styles.block, fill ? styles.fill : null, className].filter(Boolean).join(' ')}
      aria-busy={streaming || undefined}
    >
      <Text as="p" size="sm" leading="normal" color="secondary" className={styles.answer}>
        {segments.map((segment, index) => {
          if (segment.startsWith('**') && segment.endsWith('**')) {
            return (
              <strong key={index} className={styles.strong}>
                {segment.slice(2, -2)}
              </strong>
            );
          }
          if (/^\[\d+\]$/.test(segment)) {
            /*
             * A `<sup>` for the semantics, but not raised: see the stylesheet.
             * The bare number is what upstream reads out too — the Sources list
             * below numbers them identically, so the marker does not need to
             * name itself.
             */
            return (
              <sup key={index} className={`${styles.marker} ${styles.markerInline}`}>
                {segment.slice(1, -1)}
              </sup>
            );
          }
          return <Fragment key={index}>{segment}</Fragment>;
        })}
        {streaming && <span className={styles.caret} aria-hidden />}
      </Text>

      {settled && citations.length > 0 && (
        <ul className={styles.sources}>
          {citations.map((citation) => (
            <li key={citation.id} className={styles.source}>
              <span className={styles.marker}>{citation.index}</span>
              <span className={styles.sourceBody}>
                {/*
                  * Not in upstream, and the one substantive addition here: the
                  * Sources variant renders titles it holds a URL for as plain
                  * text, so the sources of an answer cannot be opened. A list
                  * of sources whose items are inert is the defect this variant
                  * exists to avoid. Resting appearance is unchanged — no link
                  * colour, no underline until hover — so this reads as the same
                  * design with the link it was missing.
                  */}
                <a
                  className={styles.sourceLink}
                  href={citation.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {citation.title}
                </a>
                <Text size="xs" weight="medium" color="tertiary" className={styles.host}>
                  {citation.host ?? hostOf(citation.url)}
                </Text>
              </span>
            </li>
          ))}
        </ul>
      )}

      {settled && hasChart && <div className={styles.chart}>{chart}</div>}

      {settled && followUps.length > 0 && (
        <div className={styles.followUps}>
          <Text as="p" size="xs" weight="medium" color="tertiary" className={styles.followUpsLabel}>
            Follow-ups
          </Text>
          <ul className={styles.followUpList}>
            {followUps.map((suggestion, index) => (
              <li
                key={suggestion.id}
                className={styles.followUpItem}
                /* A row index, not a design value — the delay itself is a token. */
                style={{ '--st-stagger': Math.min(index, MAX_STAGGER_STEPS) } as CSSProperties}
              >
                <button type="button" className={styles.followUp} onClick={() => onFollowUp?.(suggestion)}>
                  {/*
                    * Upstream's `CornerDownLeft` — the return key — has no
                    * equivalent in the Figma icon set. `chevron right` is what
                    * this system already uses for forward movement (pagination,
                    * next month, breadcrumb separator), which is what asking a
                    * follow-up is, and it takes upstream's nudge-right on hover
                    * more naturally than a return glyph does. Cheaper and more
                    * consistent than adding a 51st icon for one row.
                    */}
                  <Icon name="chevronRight" className={styles.chevron} />
                  {/*
                    * A plain span rather than `Text`, following Button's
                    * `.label`: the colour here belongs to the button's hover
                    * state, and `Text` would bring its own colour class to
                    * argue with at equal specificity.
                    */}
                  <span className={styles.followUpLabel}>{suggestion.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasActions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
